import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { formatDate } from "../lib/format";
import { MessageCircle } from "lucide-react";

const EVENTS = [
  "all",
  "tabon_locker_created",
  "tabon_locker_updated",
  "tabon_tab_low",
  "tabon_expiry_reminder",
  "tabon_feedback",
  "tabon_winback",
  "tabon_points_earned",
  "tabon_points_redeemed",
  "tabon_milestone_achieved",
  "tabon_visit_summary",
  "tabon_welcome",
  "tabon_tab_verification",
  "tabon_redeem_otp",
  "tabon_bill_receipt",
  "tabon_redemption_message",
  "tabon_loyalty_invite",
];

const EVENT_LABEL = {
  tabon_locker_created: "Tab Opened",
  tabon_locker_updated: "Tab Updated",
  tabon_tab_low: "Tab Low",
  tabon_expiry_reminder: "Expiry Reminder",
  tabon_feedback: "Feedback Request",
  tabon_winback: "Win-back Campaign",
  tabon_points_earned: "Points Earned",
  tabon_points_redeemed: "Points Redeemed",
  tabon_milestone_achieved: "Milestone Achieved",
  tabon_visit_summary: "Visit Summary",
  tabon_welcome: "Welcome",
  tabon_tab_verification: "Redemption OTP",
  tabon_redeem_otp: "Redemption OTP",
  tabon_bill_receipt: "Visit Summary",
  tabon_redemption_message: "Redemption OTP",
  tabon_loyalty_invite: "Loyalty Invite",
};

function statusBadge(s) {
  if (s === "Failed" || s === "Timeout") return "bg-white/10 text-[#A0A0A0] border-white/20";
  if (s === "Deferred") return "bg-white/10 text-[#A0A0A0] border-white/20";
  return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
}

function statusLabel(s) {
  if (s === "Failed" || s === "Timeout") return "Not delivered";
  if (s === "Deferred") return "Will retry";
  return "Sent";
}

function sentViaBadge(v) {
  if (v === "Venue Number") return "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40";
  if (v === "TabOn Shared") return "bg-white/10 text-[#A0A0A0] border-white/20";
  return "bg-white/10 text-[#A0A0A0] border-white/20";
}

export default function WhatsAppLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState("all");
  const PAGE = 100;

  // Reset to first page when filter changes
  useEffect(() => {
    setSkip(0);
    setRows([]);
    setTotal(0);
  }, [filter]);

  // Fetch rows whenever filter or skip changes
  useEffect(() => {
    const params = { skip, limit: PAGE };
    if (filter !== "all") params.event = filter;
    api.get("/whatsapp-log", { params }).then(({ data }) => {
      setTotal(data.total || 0);
      if (skip === 0) {
        // First page — replace rows
        setRows(data.rows || []);
      } else {
        // Subsequent pages — append rows
        setRows(prev => [...prev, ...(data.rows || [])]);
      }
    });
  }, [filter, skip]);

  return (
    <AppLayout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-5">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">WhatsApp Log</h1>
          <p className="text-sm text-[#A0A0A0]">
            All outbound messages via Meta WhatsApp Cloud API
            {total > 0 && ` · showing ${rows.length} of ${total}`}
          </p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {EVENTS.map((e) => (
            <button
              key={e}
              data-testid={`filter-${e}`}
              onClick={() => setFilter(e)}
              className={`px-3 py-2 rounded-lg text-xs uppercase tracking-wider font-bold border transition ${
                filter === e
                  ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                  : "bg-[#1A1A1A] border-white/10 text-[#A0A0A0] hover:text-white"
              }`}
            >
              {e === "all" ? "All Events" : EVENT_LABEL[e] || e}
            </button>
          ))}
        </div>

        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-[#A0A0A0] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Event</th>
                  <th className="text-left px-4 py-3">Message</th>
                  <th className="text-left px-4 py-3">Sent Via</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-[#A0A0A0]">
                      <MessageCircle className="mx-auto mb-2 text-[#9D00FF]" />
                      No messages yet. Configure Meta WhatsApp Cloud API in Settings.
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  // A PLATFORM failure is TabOn's — a missing/paused template, an unset WABA id.
                  // The venue can't act on it, so the row is neutralised: no template name, no Meta
                  // error, a plain "Not sent". The raw error stays in the record for TabOn (J-2b).
                  const platformFail = r.failure_class === "platform";
                  return (
                  <tr key={r.id} data-testid={`wa-row-${r.id}`} className="border-t border-white/5">
                    <td className="px-4 py-3 font-medium">{r.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-[#A0A0A0]">{r.phone || "—"}</td>
                    <td className="px-4 py-3">{platformFail ? "System message" : (EVENT_LABEL[r.event] || r.event)}</td>
                    <td className="px-4 py-3 text-[#A0A0A0] max-w-[340px] truncate">{platformFail ? "—" : r.message}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${sentViaBadge(r.sent_via)}`}>
                        {r.sent_via || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatDate(r.sent_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${statusBadge(r.status)}`}>
                        {platformFail ? "Not sent" : statusLabel(r.status)}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {rows.length < total && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setSkip(rows.length)}
              className="px-5 py-2 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white hover:border-white/30 text-xs font-semibold transition"
            >
              Load more ({total - rows.length} remaining)
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
